import { useMemo, useState } from "react";
import type { Deal, Email } from "../types";
import { useData } from "../hooks/useData";
import { api, OfflineError } from "../api/client";
import { fmtDate } from "../components/Primitives";

/**
 * Email triage on a phone: one card at a time. Saga's suggestion is the big button;
 * the other choices sit underneath. The full reading pane is one tap away ("Open").
 */
export default function PhoneInbox({ go, onChanged }: { go: (s: string, id?: string | null) => void; onChanged: () => void }) {
  const { data: emails, refetch, error } = useData<Email[]>("/api/emails?folder=review&weeks=4&limit=100");
  const { data: deals } = useData<Deal[]>("/api/deals");
  const [gone, setGone] = useState<Record<string, string>>({});
  const [picking, setPicking] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const dealName = useMemo(() => Object.fromEntries((deals || []).map(d => [d.id, d.name])), [deals]);

  const act = async (e: Email, label: string, fn: () => Promise<unknown>) => {
    setBusy(e.id); setMsg(null);
    try { await fn(); setGone(g => ({ ...g, [e.id]: label })); setPicking(null); onChanged(); }
    catch (err) {
      if (err instanceof OfflineError) { setGone(g => ({ ...g, [e.id]: label })); setPicking(null); }
      setMsg(err instanceof Error ? err.message : String(err));
    }
    finally { setBusy(null); }
  };
  const findTasks = async (e: Email) => {
    setBusy(e.id); setMsg(null);
    try {
      const full = await api.get<Email>(`/api/emails/${e.id}`);
      const r = await api.post<{ created: unknown[]; duplicates: unknown[] }>("/api/todos/extract", {
        text: `From: ${e.from_name || ""} <${e.from_email || ""}>\nSubject: ${e.subject || ""}\n\n${full.body || e.preview || ""}`,
        source_type: "email", source_id: e.id, source_label: `Email: ${e.subject || "(no subject)"}`, deal_id: e.deal_id || e.ai_deal_id || null,
      });
      setMsg(r.created.length ? `${r.created.length} suggested task${r.created.length === 1 ? "" : "s"} — see Tasks → Suggested`
        : r.duplicates.length ? "Nothing new — those are already tasks" : "No tasks found in this email");
    } catch (err) { setMsg(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(null); }
  };

  if (error && !emails) return <div className="ph-pad"><div className="ph-note err">{error}</div></div>;
  if (!emails) return <div className="ph-pad"><div className="ph-skel" /><div className="ph-skel" /></div>;
  const left = emails.filter(e => !gone[e.id]);
  const filed = Object.keys(gone).length;

  return (
    <div className="ph-pad">
      <div className="ph-sub">
        <span>{left.length ? `${left.length} to file` : "All filed"}{filed ? ` · ${filed} done now` : ""}</span>
        <button className="ph-link" onClick={() => go("inbox-all")}>All email ›</button>
      </div>
      {msg && <div className="ph-note">{msg}</div>}
      {left.length === 0 && (
        <div className="ph-card ph-empty">
          Inbox zero. Every email from the last four weeks is filed.
          <button className="ph-btn ghost" onClick={() => { setGone({}); refetch(); }}>Check again</button>
        </div>
      )}
      {left.slice(0, 20).map(e => {
        const sug = e.ai_deal_id ? dealName[e.ai_deal_id] : null;
        const list = (deals || []).filter(d => !q || d.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
        return (
          <article key={e.id} className="ph-card ph-mail">
            <button className="ph-mail-h" onClick={() => go("inbox-all", e.id)}>
              <span className="ph-row-t">{e.from_name || e.from_email}</span>
              <span className="ph-due">{e.date ? fmtDate(e.date) : ""}</span>
            </button>
            <div className="ph-mail-s">{e.subject || "(no subject)"}</div>
            <div className="ph-mail-p">{e.preview}</div>
            {sug && <div className="ph-mail-ai">✦ Saga suggests <b>{sug}</b>{e.ai_reason ? <> — {e.ai_reason}</> : null}</div>}
            {picking === e.id ? (
              <div className="ph-pick">
                <input className="ph-input" autoFocus placeholder="Find a deal…" value={q} onChange={ev => setQ(ev.target.value)} />
                <div className="ph-pick-list">
                  {list.map(d => (
                    <button key={d.id} disabled={busy === e.id}
                      onClick={() => act(e, `Filed under ${d.name}`, () => api.put(`/api/emails/${e.id}/route`, { deal_id: d.id, status: "manual" }))}>
                      {d.name}<span>{d.principal_name || ""}</span>
                    </button>
                  ))}
                  {!list.length && <div className="ph-note">No deal matches “{q}”.</div>}
                </div>
                <button className="ph-btn ghost" onClick={() => setPicking(null)}>Cancel</button>
              </div>
            ) : (
              <div className="ph-mail-a">
                {sug ? (
                  <button className="ph-btn primary" disabled={busy === e.id}
                    onClick={() => act(e, `Filed under ${sug}`, () => api.put(`/api/emails/${e.id}/route`, { deal_id: e.ai_deal_id, status: "confirmed" }))}>
                    File under {sug}
                  </button>
                ) : (
                  <button className="ph-btn primary" onClick={() => { setQ(""); setPicking(e.id); }}>Choose a deal</button>
                )}
                <div className="ph-mail-more">
                  {sug && <button onClick={() => { setQ(""); setPicking(e.id); }}>Other deal</button>}
                  <button onClick={() => go("inbox-all", e.id)}>Reply</button>
                  <button disabled={busy === e.id} onClick={() => act(e, "Filed without a deal", () => api.put(`/api/emails/${e.id}/route`, { deal_id: null, status: "manual" }))}>No deal</button>
                  <button disabled={busy === e.id} onClick={() => findTasks(e)}>Find tasks</button>
                  <button disabled={busy === e.id} onClick={() => act(e, "Dismissed", () => api.put(`/api/emails/${e.id}/private`, {}))}>Not business</button>
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
